import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [checked, setChecked] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from Google OAuth, skip the /me check here —
    // the GoogleCallback screen exchanges the session_id and establishes the session first.
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      return;
    }
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await refreshMe();
    return data;
  }, [refreshMe]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    await refreshMe();
    return data;
  }, [refreshMe]);

  const logout = useCallback(async () => {
    // Logout failures are non-critical: we always clear local auth state.
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network/API errors on logout */
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, checked, login, register, logout, refreshMe }),
    [user, checked, login, register, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
